import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { 
  Video, 
  Download, 
  Play, 
  X, 
  Calendar, 
  Clock, 
  HardDrive,
  Film,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  Shield,
  Grid,
  List,
  Trash2,
  CheckSquare,
  Square,
  Check,
  Lock,
  EyeOff,
  Loader2,
  Home,
  ChevronDown,
  CheckCircle,
  Sun,
  Moon
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useMediaServerConnection } from "@/hooks/useMediaServerConnection";
import "./VideoLibrary.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NSAS_VIDEO_SERVER_URL = "https://srv-ops.nsas.tech";
const NSAS_WEBRTC_PLAYER_URL = "https://srv-ops.nsas.tech:8443";
const NSAS_LOGO = "https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png";

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes < 0) return "Unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Format duration
const formatDuration = (ms) => {
  if (!ms || ms < 0) return "Unknown";
  const seconds = Math.floor(ms / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format date (UTC/Zulu)
const formatDate = (timestamp) => {
  if (!timestamp) return "Unknown";
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC'
  }) + 'Z';
};

// Format date only (no time) - UTC/Zulu
const formatDateOnly = (timestamp) => {
  if (!timestamp) return "Unknown";
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

// Format time only - UTC/Zulu
const formatTime = (timestamp) => {
  if (!timestamp) return "??:??";
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC'
  }) + 'Z';
};

// Get formatted recording display name: "SPARROW 3 - 06 Mar 2026 - 14:30Z / 15:45Z"
const getRecordingDisplayName = (recording) => {
  const streamName = recording.streamId.toUpperCase();
  const dateStr = formatDateOnly(recording.startTime);
  const startTime = formatTime(recording.startTime);
  const endTime = formatTime(recording.startTime + recording.duration);
  return `${streamName} - ${dateStr} - ${startTime} / ${endTime}`;
};

// Get FTX designator from stream name
const getFtxDesignator = (streamId) => {
  const streamUpper = streamId.toUpperCase();
  if (streamUpper.includes('SPARROW')) return 'SPARROW3';
  if (streamUpper.includes('OSPREY')) return 'OSPREY4';
  return streamUpper.replace(/[^A-Z0-9]/g, '');
};

// Generate redacted filename: FTXDESIGNATOR_DATE_STARTTIME_STOPTIME.mp4
const getRedactedFilename = (recording) => {
  const ftx = getFtxDesignator(recording.streamId);
  const date = new Date(recording.startTime);
  const endDate = new Date(recording.startTime + recording.duration);
  
  // Format: YYYYMMDD
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  
  // Format times: HHMM (UTC)
  const startTime = date.toISOString().slice(11, 16).replace(':', '');
  const stopTime = endDate.toISOString().slice(11, 16).replace(':', '');
  
  return `${ftx}_${dateStr}_${startTime}Z_${stopTime}Z.mp4`;
};

// Get stream quality label
const getQualityLabel = (vodName) => {
  if (vodName.includes('1080p')) return '1080p';
  if (vodName.includes('720p')) return '720p';
  if (vodName.includes('480p')) return '480p';
  return 'Original';
};

// Get quality badge color
const getQualityColor = (quality) => {
  switch (quality) {
    case '1080p': return '#22c55e';
    case '720p': return '#3b82f6';
    case '480p': return '#f59e0b';
    default: return '#8b5cf6';
  }
};

// Get thumbnail URLs - returns array of possible thumbnail locations to try
// Primary: Backend proxy (works without VPN)
// Fallback: Direct URL from NSAS Video Server (requires VPN)
const getThumbnailUrls = (recording) => {
  const urls = [];
  const vodNameWithoutExt = recording.vodName.replace(/\.[^.]+$/, '');
  const isRedacted = recording.folder === 'redacted' || recording.filePath?.includes('redacted');
  
  // Primary: Use backend proxy (always works, no VPN needed)
  if (isRedacted) {
    urls.push(`${API_URL}/api/videos/thumbnail/redacted/${vodNameWithoutExt}.jpg`);
  } else {
    urls.push(`${API_URL}/api/videos/thumbnail/${vodNameWithoutExt}.jpg`);
  }
  
  // Fallback: Direct URL from NSAS Video Server (returned by API, requires VPN)
  if (recording.thumbnailUrl) {
    urls.push(recording.thumbnailUrl);
  }
  
  return urls;
};

// Legacy function for backward compatibility
const getThumbnailUrl = (recording) => {
  return getThumbnailUrls(recording)[0];
};

// Thumbnail Image component with multiple URL fallbacks
const ThumbnailImage = ({ recording, className, size = 'large', refreshKey = 0 }) => {
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const urls = getThumbnailUrls(recording);
  
  // Reset state when refreshKey changes (thumbnails were regenerated)
  useEffect(() => {
    if (refreshKey > 0) {
      setCurrentUrlIndex(0);
      setShowPlaceholder(false);
    }
  }, [refreshKey]);
  
  const handleError = () => {
    if (currentUrlIndex < urls.length - 1) {
      // Try next URL
      setCurrentUrlIndex(prev => prev + 1);
    } else {
      // All URLs failed, show placeholder
      setShowPlaceholder(true);
    }
  };
  
  if (showPlaceholder) {
    return (
      <div className={`thumbnail-placeholder ${size === 'small' ? 'small' : ''}`}>
        <Play size={size === 'small' ? 20 : 48} />
      </div>
    );
  }
  
  return (
    <img 
      src={`${urls[currentUrlIndex]}${refreshKey > 0 ? `?t=${refreshKey}` : ''}`} 
      alt={recording.vodName}
      className={className}
      onError={handleError}
    />
  );
};

const VideoLibrary = () => {
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);
  const [filteredRecordings, setFilteredRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStream, setSelectedStream] = useState("all");
  const [selectedQuality, setSelectedQuality] = useState("Original");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoStatus, setVideoStatus] = useState('loading');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedRecordings, setSelectedRecordings] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [thumbnailRefreshKey, setThumbnailRefreshKey] = useState(0); // Force thumbnail refresh
  
  // Connection error is only for video playback, not for listings
  // Backend can always fetch listings via SSH
  const [connectionError, setConnectionError] = useState(false);
  
  // Use the shared media server connection hook for checking browser-side connectivity
  // autoCheck=false - we only check when user tries to play a video
  // Thumbnails work via proxy without VPN
  const { 
    isConnected: mediaServerConnected, 
    isChecking: checkingConnection,
    checkConnection,
    retry: retryConnection 
  } = useMediaServerConnection(false);
  
  // Track if we've checked connection for video playback
  const [playbackConnectionChecked, setPlaybackConnectionChecked] = useState(false);
  
  // Redacted video processing state
  const [processingRedacted, setProcessingRedacted] = useState(new Set());
  const [redactedProgress, setRedactedProgress] = useState({}); // vodId -> progress percentage or status
  const [redactedReady, setRedactedReady] = useState({}); // vodId -> download URL when ready
  const [openDropdown, setOpenDropdown] = useState(null); // vodId of open dropdown
  
  // Mobile download PIN state
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinCode, setPinCode] = useState('');
  const [pinError, setPinError] = useState('');
  const [pendingDownload, setPendingDownload] = useState(null);
  const [pendingDownloadType, setPendingDownloadType] = useState('original'); // 'original' or 'redacted'
  
  const DOWNLOAD_PIN = '6969'; // PIN code for mobile downloads
  
  // Use global theme context
  const { theme, toggleTheme } = useTheme();
  
  // AI Person Counting state
  const [personCountEnabled, setPersonCountEnabled] = useState(false);
  const [personCount, setPersonCount] = useState(null);
  const [personCountLoading, setPersonCountLoading] = useState(false);
  const [personCountConfidence, setPersonCountConfidence] = useState(null);
  const videoRef = useRef(null);
  const countIntervalRef = useRef(null);

  // AI Person Counting - capture frame and analyze
  const captureAndCountPersons = async () => {
    if (!videoRef.current || personCountLoading) return;
    
    try {
      setPersonCountLoading(true);
      
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert to base64 (remove data:image/png;base64, prefix)
      const base64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      
      // Send to backend for AI analysis
      const response = await fetch(`${API_URL}/api/videos/count-persons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: base64 })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setPersonCount(data.count);
        setPersonCountConfidence(data.confidence);
      }
    } catch (error) {
      console.error('Person counting error:', error);
    } finally {
      setPersonCountLoading(false);
    }
  };

  // Start/stop person counting interval
  const togglePersonCounting = () => {
    if (personCountEnabled) {
      // Stop counting
      if (countIntervalRef.current) {
        clearInterval(countIntervalRef.current);
        countIntervalRef.current = null;
      }
      setPersonCountEnabled(false);
      setPersonCount(null);
      setPersonCountConfidence(null);
    } else {
      // Start counting
      setPersonCountEnabled(true);
      captureAndCountPersons(); // Immediate first count
      countIntervalRef.current = setInterval(captureAndCountPersons, 3000); // Every 3 seconds
    }
  };

  // Cleanup person counting on video close
  useEffect(() => {
    return () => {
      if (countIntervalRef.current) {
        clearInterval(countIntervalRef.current);
      }
    };
  }, []);

  // Stop person counting when video modal closes
  useEffect(() => {
    if (!selectedVideo) {
      if (countIntervalRef.current) {
        clearInterval(countIntervalRef.current);
        countIntervalRef.current = null;
      }
      setPersonCountEnabled(false);
      setPersonCount(null);
    }
  }, [selectedVideo]);

  // Check for existing redacted files on mount
  useEffect(() => {
    const checkExistingRedacted = async () => {
      // Load from localStorage first
      const cached = localStorage.getItem('redactedVideos');
      if (cached) {
        try {
          setRedactedReady(JSON.parse(cached));
        } catch (e) {
          console.error('Failed to parse cached redacted videos');
        }
      }
      
      // Check for any videos that were being processed before refresh
      const processingCache = localStorage.getItem('processingRedacted');
      if (processingCache) {
        try {
          const processingList = JSON.parse(processingCache);
          if (processingList.length > 0) {
            // Restore processing state and resume polling
            processingList.forEach(item => {
              resumeRedactedPolling(item.vodId, item.recording);
            });
          }
        } catch (e) {
          console.error('Failed to restore processing state');
        }
      }
    };
    checkExistingRedacted();
  }, []);
  
  // Save processing state to localStorage
  useEffect(() => {
    if (processingRedacted.size > 0) {
      // We need to store the recording data along with vodId
      const processingList = [];
      processingRedacted.forEach(vodId => {
        // Find recording in filteredRecordings or recordings
        const recording = recordings.find(r => r.vodId === vodId);
        if (recording) {
          processingList.push({ vodId, recording });
        }
      });
      localStorage.setItem('processingRedacted', JSON.stringify(processingList));
    } else {
      localStorage.removeItem('processingRedacted');
    }
  }, [processingRedacted, recordings]);
  
  // Save redacted state to localStorage
  useEffect(() => {
    if (Object.keys(redactedReady).length > 0) {
      localStorage.setItem('redactedVideos', JSON.stringify(redactedReady));
    }
  }, [redactedReady]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdown && !e.target.closest('.redacted-dropdown-container') && !e.target.closest('.list-redacted-dropdown')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);
  
  // Detect mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  };
  
  // Handle regular download click
  const handleDownloadClick = (e, recording) => {
    if (isMobileDevice()) {
      e.preventDefault();
      setPendingDownload(recording);
      setPendingDownloadType('original');
      setPinCode('');
      setPinError('');
      setShowPinModal(true);
    }
    // On desktop, let the default link behavior happen
  };
  
  // Handle redacted download click
  const handleRedactedDownloadClick = async (e, recording) => {
    e.preventDefault();
    
    if (isMobileDevice()) {
      setPendingDownload(recording);
      setPendingDownloadType('redacted');
      setPinCode('');
      setPinError('');
      setShowPinModal(true);
      return;
    }
    
    // Start redacted download process
    await initiateRedactedDownload(recording);
  };
  
  // Initiate redacted video processing and download
  const initiateRedactedDownload = async (recording) => {
    const vodId = recording.vodId;
    
    // Mark as processing with initial progress
    setProcessingRedacted(prev => new Set([...prev, vodId]));
    setRedactedProgress(prev => ({ ...prev, [vodId]: 0 }));
    
    try {
      // Start the redaction job
      const startResponse = await fetch(`${API_URL}/api/videos/redact/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vodName: recording.vodName,
          filePath: recording.filePath,
          streamId: recording.streamId
        })
      });
      
      if (!startResponse.ok) {
        const errorData = await startResponse.json();
        throw new Error(errorData.detail || 'Failed to start processing');
      }
      
      const startData = await startResponse.json();
      
      // If already complete, we're done
      if (startData.status === 'complete') {
        setRedactedProgress(prev => ({ ...prev, [vodId]: 100 }));
        setRedactedReady(prev => ({ 
          ...prev, 
          [vodId]: {
            url: startData.download_url,
            filename: getRedactedFilename(recording),
            recording: recording
          }
        }));
        setTimeout(() => {
          setProcessingRedacted(prev => {
            const newSet = new Set(prev);
            newSet.delete(vodId);
            return newSet;
          });
        }, 1000);
        return;
      }
      
      // Poll for status
      const pollStatus = async () => {
        try {
          const statusResponse = await fetch(`${API_URL}/api/videos/redact/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ vodName: recording.vodName })
          });
          
          if (!statusResponse.ok) {
            throw new Error('Status check failed');
          }
          
          const statusData = await statusResponse.json();
          
          if (statusData.status === 'complete') {
            // Done! Update state
            setRedactedProgress(prev => ({ ...prev, [vodId]: 100 }));
            setRedactedReady(prev => ({ 
              ...prev, 
              [vodId]: {
                url: statusData.download_url,
                filename: getRedactedFilename(recording),
                recording: recording
              }
            }));
            setTimeout(() => {
              setProcessingRedacted(prev => {
                const newSet = new Set(prev);
                newSet.delete(vodId);
                return newSet;
              });
              setRedactedProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[vodId];
                return newProgress;
              });
            }, 1500);
            return; // Stop polling
          } else if (statusData.status === 'processing') {
            // Update progress and continue polling
            setRedactedProgress(prev => ({ ...prev, [vodId]: statusData.progress || 50 }));
            setTimeout(pollStatus, 2000); // Poll every 2 seconds
          } else if (statusData.status === 'not_found') {
            // Job may have failed or not started properly
            throw new Error('Processing job not found');
          }
        } catch (pollErr) {
          console.error('Status poll error:', pollErr);
          setRedactedProgress(prev => ({ ...prev, [vodId]: -1 }));
          setTimeout(() => {
            setProcessingRedacted(prev => {
              const newSet = new Set(prev);
              newSet.delete(vodId);
              return newSet;
            });
          }, 3000);
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollStatus, 1500);
      
    } catch (err) {
      console.error('Redacted download error:', err);
      setRedactedProgress(prev => ({ ...prev, [vodId]: -1 })); // -1 indicates error
      
      // Clear error state after a moment
      setTimeout(() => {
        setProcessingRedacted(prev => {
          const newSet = new Set(prev);
          newSet.delete(vodId);
          return newSet;
        });
        setRedactedProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[vodId];
          return newProgress;
        });
      }, 5000);
    }
  };
  
  // Resume polling for a video that was being processed before page refresh
  const resumeRedactedPolling = (vodId, recording) => {
    // Mark as processing
    setProcessingRedacted(prev => new Set([...prev, vodId]));
    setRedactedProgress(prev => ({ ...prev, [vodId]: 0 }));
    
    const pollStatus = async () => {
      try {
        const statusResponse = await fetch(`${API_URL}/api/videos/redact/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vodName: recording.vodName })
        });
        
        if (!statusResponse.ok) {
          throw new Error('Status check failed');
        }
        
        const statusData = await statusResponse.json();
        
        if (statusData.status === 'complete') {
          // Done! Update state
          setRedactedProgress(prev => ({ ...prev, [vodId]: 100 }));
          setRedactedReady(prev => ({ 
            ...prev, 
            [vodId]: {
              url: statusData.download_url,
              filename: getRedactedFilename(recording),
              recording: recording
            }
          }));
          setTimeout(() => {
            setProcessingRedacted(prev => {
              const newSet = new Set(prev);
              newSet.delete(vodId);
              return newSet;
            });
            setRedactedProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[vodId];
              return newProgress;
            });
          }, 1500);
          return; // Stop polling
        } else if (statusData.status === 'processing') {
          // Update progress and continue polling
          setRedactedProgress(prev => ({ ...prev, [vodId]: statusData.progress || 50 }));
          setTimeout(pollStatus, 2000); // Poll every 2 seconds
        } else if (statusData.status === 'not_found') {
          // Job finished or failed - clear state
          setProcessingRedacted(prev => {
            const newSet = new Set(prev);
            newSet.delete(vodId);
            return newSet;
          });
          setRedactedProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[vodId];
            return newProgress;
          });
        }
      } catch (pollErr) {
        console.error('Resume poll error:', pollErr);
        // Clear state on error
        setProcessingRedacted(prev => {
          const newSet = new Set(prev);
          newSet.delete(vodId);
          return newSet;
        });
        setRedactedProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[vodId];
          return newProgress;
        });
      }
    };
    
    // Start polling immediately
    pollStatus();
  };
  
  // Download ready redacted file - fetch as blob with progress tracking
  const downloadRedactedFile = async (vodId) => {
    const redacted = redactedReady[vodId];
    if (!redacted) return;
    
    setOpenDropdown(null);
    
    // Create a persistent toast with progress bar (using toast() instead of toast.loading to avoid spinner)
    const toastId = toast(
      <div className="download-progress-toast">
        <div className="download-progress-header">
          <Download size={18} />
          <span>Downloading...</span>
        </div>
        <div className="download-progress-filename">{redacted.filename}</div>
        <div className="download-progress-bar-bg">
          <div className="download-progress-bar-fill" style={{ width: '0%' }} />
        </div>
        <div className="download-progress-text">0%</div>
      </div>,
      { duration: Infinity, position: 'top-right' }
    );
    
    try {
      // Show downloading indicator on button
      setRedactedProgress(prev => ({ ...prev, [vodId]: 'downloading' }));
      setProcessingRedacted(prev => new Set([...prev, vodId]));
      
      // Fetch with progress tracking
      const response = await fetch(redacted.url);
      if (!response.ok) throw new Error('Download failed');
      
      const contentLength = response.headers.get('content-length');
      const total = parseInt(contentLength, 10) || 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        received += value.length;
        
        // Calculate progress
        const progress = total ? Math.round((received / total) * 100) : 0;
        const downloadedMB = (received / (1024 * 1024)).toFixed(1);
        const totalMB = total ? (total / (1024 * 1024)).toFixed(1) : '?';
        
        // Update toast with progress (using toast() to avoid spinner)
        toast(
          <div className="download-progress-toast">
            <div className="download-progress-header">
              <Download size={18} />
              <span>Downloading...</span>
            </div>
            <div className="download-progress-filename">{redacted.filename}</div>
            <div className="download-progress-bar-bg">
              <div className="download-progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="download-progress-text">{progress}% ({downloadedMB} / {totalMB} MB)</div>
          </div>,
          { id: toastId, duration: Infinity }
        );
      }
      
      // Combine chunks into blob
      const blob = new Blob(chunks);
      
      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = redacted.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      // Show success notification
      toast.success(
        <div className="download-progress-toast">
          <div className="download-progress-header success">
            <CheckCircle size={18} />
            <span>Download complete!</span>
          </div>
          <div className="download-progress-filename">{redacted.filename}</div>
        </div>,
        { id: toastId, duration: 4000 }
      );
      
      // Clear downloading state
      setProcessingRedacted(prev => {
        const newSet = new Set(prev);
        newSet.delete(vodId);
        return newSet;
      });
      setRedactedProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[vodId];
        return newProgress;
      });
      
    } catch (err) {
      console.error('Download error:', err);
      
      // Show error notification
      toast.error(
        <div className="download-progress-toast">
          <div className="download-progress-header error">
            <AlertTriangle size={18} />
            <span>Download failed</span>
          </div>
          <div className="download-progress-text">Check your VPN connection</div>
        </div>,
        { id: toastId, duration: 5000 }
      );
      
      // Clear state on error
      setProcessingRedacted(prev => {
        const newSet = new Set(prev);
        newSet.delete(vodId);
        return newSet;
      });
      setRedactedProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[vodId];
        return newProgress;
      });
    }
  };
  
  // Verify PIN and initiate download
  const verifyPinAndDownload = async () => {
    if (pinCode === DOWNLOAD_PIN) {
      setShowPinModal(false);
      setPinCode('');
      setPinError('');
      
      if (pendingDownload) {
        if (pendingDownloadType === 'redacted') {
          // Initiate redacted download
          await initiateRedactedDownload(pendingDownload);
        } else {
          // Regular download
          const link = document.createElement('a');
          link.href = getDownloadUrl(pendingDownload);
          link.download = pendingDownload.vodName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      setPendingDownload(null);
      setPendingDownloadType('original');
    } else {
      setPinError('Incorrect PIN code');
      setPinCode('');
    }
  };
  
  // Modal state for draggable/resizable
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalSize, setModalSize] = useState({ width: 854, height: 480 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const videoTimeoutRef = useRef(null);

  // Toggle recording selection
  const toggleRecordingSelection = (vodId) => {
    setSelectedRecordings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vodId)) {
        newSet.delete(vodId);
      } else {
        newSet.add(vodId);
      }
      return newSet;
    });
  };

  // Select all filtered recordings
  const selectAllRecordings = () => {
    if (selectedRecordings.size === filteredRecordings.length) {
      setSelectedRecordings(new Set());
    } else {
      setSelectedRecordings(new Set(filteredRecordings.map(r => r.vodId || r.vodName)));
    }
  };

  // Delete selected recordings via backend SSH
  const deleteSelectedRecordings = async () => {
    if (selectedRecordings.size === 0) return;
    
    setIsDeleting(true);
    const errors = [];
    
    for (const vodId of selectedRecordings) {
      try {
        // Find the recording to get filename and folder
        const recording = recordings.find(r => (r.vodId || r.vodName) === vodId);
        if (!recording) {
          errors.push(vodId);
          continue;
        }
        
        const filename = recording.vodName;
        const folder = recording.folder || 'main';
        
        const response = await fetch(`${API_URL}/api/video-library/delete/${encodeURIComponent(filename)}?folder=${folder}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) {
          const data = await response.json();
          console.error(`Delete failed for ${filename}:`, data.detail);
          errors.push(filename);
        }
      } catch (err) {
        console.error(`Delete error:`, err);
        errors.push(vodId);
      }
    }
    
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setSelectedRecordings(new Set());
    
    // Refresh the list
    fetchRecordings();
    
    if (errors.length > 0) {
      alert(`Failed to delete ${errors.length} recording(s): ${errors.join(', ')}`);
    }
  };

  // Fetch recordings from backend (which scans filesystem via SSH)
  // Backend uses SSH to scan the media server - no VPN required for listing
  const fetchRecordings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use longer timeout for backend SSH operations (30 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // Fetch from our backend which scans the media server filesystem via SSH
      const response = await fetch(`${API_URL}/api/video-library/filesystem-recordings`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error) {
          console.error('Backend error:', data.error);
          setRecordings([]);
          return;
        }
        
        // Filter out invalid recordings and sort by date
        const validRecordings = (data.recordings || [])
          .filter(r => r.fileSize > 100000) // Only files > 100KB
          .sort((a, b) => b.startTime - a.startTime);
        
        setRecordings(validRecordings);
        
        // Check for missing thumbnails in background
        fetch(`${API_URL}/api/videos/check-thumbnails`, { method: 'POST' })
          .then(res => res.json())
          .then(result => {
            if (result.generated > 0) {
              console.log(`Generated ${result.generated} missing thumbnails`);
              setTimeout(() => {
                setThumbnailRefreshKey(prev => prev + 1);
              }, 1000);
            }
          })
          .catch(err => console.log('Thumbnail check skipped:', err.message));
        
        // Check for any active redaction processes
        fetch(`${API_URL}/api/videos/redact/active`)
          .then(res => res.json())
          .then(result => {
            if (result.status === 'ok' && result.active && result.active.length > 0) {
              console.log(`Found ${result.active.length} active redaction process(es)`);
              result.active.forEach(activeProcess => {
                const recording = validRecordings.find(r => r.vodName === activeProcess.vodName);
                if (recording && !processingRedacted.has(recording.vodId || recording.vodName)) {
                  console.log(`Resuming polling for ${activeProcess.vodName}`);
                  resumeRedactedPolling(recording.vodId || recording.vodName, recording);
                }
              });
            }
          })
          .catch(err => console.log('Active redaction check skipped:', err.message));
          
      } else {
        setRecordings([]);
      }
    } catch (err) {
      console.error("Error fetching recordings:", err);
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch recordings on mount - backend uses SSH, no VPN needed for listing
  useEffect(() => {
    fetchRecordings();
  }, []);

  // Filter recordings
  useEffect(() => {
    let filtered = [...recordings];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.vodName.toLowerCase().includes(term) ||
        r.streamId.toLowerCase().includes(term) ||
        r.streamName?.toLowerCase().includes(term)
      );
    }
    
    if (selectedStream !== "all") {
      filtered = filtered.filter(r => r.streamId === selectedStream);
    }
    
    if (selectedQuality !== "all") {
      filtered = filtered.filter(r => getQualityLabel(r.vodName) === selectedQuality);
    }
    
    setFilteredRecordings(filtered);
  }, [recordings, searchTerm, selectedStream, selectedQuality]);

  // Get unique streams for filter
  const uniqueStreams = [...new Set(recordings.map(r => r.streamId))];

  // Center modal on open
  const centerModal = () => {
    const width = 854;
    const height = 480;
    setModalSize({ width, height });
    setModalPosition({
      x: (window.innerWidth - width) / 2,
      y: (window.innerHeight - height - 100) / 2
    });
  };

  // Open video modal - check VPN connection first
  const openVideo = async (video) => {
    setVideoStatus('loading');
    setSelectedVideo(video);
    centerModal();
    
    // Check VPN connection before attempting to play video
    if (!playbackConnectionChecked) {
      const connectionResult = await checkConnection();
      setPlaybackConnectionChecked(true);
      
      if (!connectionResult.connected) {
        setVideoStatus('error');
        return;
      }
    }
    
    // Increase timeout from 15s to 30s to allow more time for VPN connections
    if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
    videoTimeoutRef.current = setTimeout(() => {
      setVideoStatus(prev => prev === 'loading' ? 'error' : prev);
    }, 30000);
  };

  // Close video modal
  const closeVideo = () => {
    if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
    setSelectedVideo(null);
    setVideoStatus('loading');
  };

  // Drag handlers
  const handleDragStart = (e) => {
    if (e.target.closest('.video-modal-close') || e.target.closest('.resize-handle')) return;
    setIsDragging(true);
    setIsInteracting(true);
    setDragOffset({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    });
  };

  const handleDrag = useCallback((e) => {
    if (!isDragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - modalSize.width, e.clientX - dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - modalSize.height - 100, e.clientY - dragOffset.y));
    setModalPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, modalSize]);

  const handleDragEnd = useCallback((e) => {
    e.stopPropagation();
    setIsDragging(false);
    setIsResizing(false);
    setTimeout(() => setIsInteracting(false), 100);
  }, []);

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setIsInteracting(true);
  };

  const handleResize = useCallback((e) => {
    if (!isResizing) return;
    const minWidth = 480;
    const maxWidth = window.innerWidth - modalPosition.x - 20;
    const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX - modalPosition.x));
    const newHeight = newWidth * (9 / 16);
    
    if (modalPosition.y + newHeight > window.innerHeight - 120) {
      const adjustedHeight = window.innerHeight - modalPosition.y - 120;
      const adjustedWidth = adjustedHeight * (16 / 9);
      setModalSize({ width: adjustedWidth, height: adjustedHeight });
    } else {
      setModalSize({ width: newWidth, height: newHeight });
    }
  }, [isResizing, modalPosition]);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', isDragging ? handleDrag : handleResize);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', isDragging ? handleDrag : handleResize);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, isResizing, handleDrag, handleResize, handleDragEnd]);

  // Get video URL - for recorded videos, use download URL; for live, use WebRTC
  const getVideoUrl = (video) => {
    // Use downloadUrl from API if available
    if (video.downloadUrl) {
      return video.downloadUrl;
    }
    // Fallback: construct URL
    return `${NSAS_VIDEO_SERVER_URL}/recordings/${video.vodName}`;
  };

  // Get download URL
  const getDownloadUrl = (video) => {
    // Use downloadUrl from API if available
    if (video.downloadUrl) {
      return video.downloadUrl;
    }
    // Fallback: construct URL
    if (video.folder === 'redacted') {
      return `${NSAS_VIDEO_SERVER_URL}/recordings/redacted/${video.vodName}`;
    }
    return `${NSAS_VIDEO_SERVER_URL}/recordings/${video.vodName}`;
  };

  return (
    <div className={`video-library-page ${theme}`}>
      {/* Fixed Header Bar with Logo and Zulu Time */}
      <div className="video-library-fixed-header">
        <div className="fixed-header-left">
          <div className="nsas-logo-header" onClick={() => navigate('/')} data-testid="nsas-logo-home">
            <img src={NSAS_LOGO} alt="NSAS" className="nsas-logo" />
          </div>
          <div className="header-title-section">
            <Film size={24} />
            <h1>Video Library</h1>
          </div>
        </div>
        <div className="fixed-header-right">
          <div className="zulu-time-display">
            <Clock size={16} />
            <span>{new Date().toLocaleString('en-GB', { 
              day: '2-digit', 
              month: 'short', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              timeZone: 'UTC' 
            })}Z</span>
          </div>
          <button 
            className="theme-toggle-btn" 
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </div>
      
      <div className="video-library-header">
        <div className="video-library-title">
          <span className="recording-count">{filteredRecordings.length} recordings</span>
        </div>
        
        <div className="video-library-controls">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search recordings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <Filter size={18} />
            <select 
              value={selectedStream} 
              onChange={(e) => setSelectedStream(e.target.value)}
            >
              <option value="all">All Streams</option>
              {uniqueStreams.map(stream => (
                <option key={stream} value={stream}>{stream.toUpperCase()}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <select 
              value={selectedQuality} 
              onChange={(e) => setSelectedQuality(e.target.value)}
            >
              <option value="all">All Quality</option>
              <option value="1080p">1080p</option>
              <option value="720p">720p</option>
              <option value="480p">480p</option>
              <option value="Original">Original</option>
            </select>
          </div>
          
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <Grid size={18} />
            </button>
            <button 
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List size={18} />
            </button>
          </div>
          
          <button className="refresh-btn" onClick={fetchRecordings} disabled={loading}>
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Selection toolbar */}
      {filteredRecordings.length > 0 && (
        <div className="selection-toolbar">
          <button 
            className="select-all-btn"
            onClick={selectAllRecordings}
          >
            {selectedRecordings.size === filteredRecordings.length ? (
              <CheckSquare size={18} />
            ) : (
              <Square size={18} />
            )}
            {selectedRecordings.size === filteredRecordings.length ? 'Deselect All' : 'Select All'}
          </button>
          
          {selectedRecordings.size > 0 && (
            <>
              <span className="selection-count">
                {selectedRecordings.size} selected
              </span>
              <button 
                className="delete-selected-btn"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
              >
                <Trash2 size={18} />
                Delete Selected
              </button>
            </>
          )}
        </div>
      )}

      <div className="video-library-content">
        {loading ? (
          <div className="loading-state">
            <div className="nsas-loader-3d">
              <img src={NSAS_LOGO} alt="Loading" className="nsas-logo-spin" />
            </div>
            <p>Loading recordings...</p>
          </div>
        ) : filteredRecordings.length === 0 ? (
          <div className="empty-state">
            <Video size={64} />
            <h3>No recordings found</h3>
            <p>Try adjusting your filters or search term</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="recordings-grid">
            {filteredRecordings.map((recording) => {
              const quality = getQualityLabel(recording.vodName);
              const isSelected = selectedRecordings.has(recording.vodId || recording.vodName);
              return (
                <div key={recording.vodId || recording.vodName} className={`recording-card ${isSelected ? 'selected' : ''}`}>
                  <div 
                    className="recording-checkbox"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRecordingSelection(recording.vodId || recording.vodName);
                    }}
                  >
                    {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                  </div>
                  <div className="recording-thumbnail" onClick={() => openVideo(recording)}>
                    <ThumbnailImage 
                      recording={recording} 
                      className="thumbnail-image"
                      size="large"
                      refreshKey={thumbnailRefreshKey}
                    />
                    <div className="thumbnail-play-overlay">
                      <Play size={48} />
                    </div>
                    <div className="recording-duration">
                      {formatDuration(recording.duration)}
                    </div>
                    <div 
                      className="recording-quality"
                      style={{ backgroundColor: getQualityColor(quality) }}
                    >
                      {quality}
                    </div>
                  </div>
                  
                  <div className="recording-info">
                    <h3 className="recording-name" title={getRecordingDisplayName(recording)}>
                      {getRecordingDisplayName(recording)}
                    </h3>
                    
                    <div className="recording-meta">
                      <span className="meta-item">
                        <Clock size={14} />
                        {formatDuration(recording.duration)}
                      </span>
                      <span className="meta-item">
                        <HardDrive size={14} />
                        {formatFileSize(recording.fileSize)}
                      </span>
                    </div>
                    
                    <div className="recording-actions">
                      <button 
                        className="action-btn play-btn"
                        onClick={() => openVideo(recording)}
                        data-testid="play-btn"
                      >
                        <Play size={16} />
                        Play
                      </button>
                      <a 
                        href={getDownloadUrl(recording)}
                        className="action-btn download-btn"
                        download={recording.vodName}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleDownloadClick(e, recording)}
                        data-testid="download-original-btn"
                      >
                        <Download size={16} />
                        Original
                      </a>
                      
                      {/* Redacted button with progress bar or dropdown */}
                      {redactedReady[recording.vodId] && !processingRedacted.has(recording.vodId) ? (
                        // Show dropdown when redacted version is ready
                        <div className="redacted-dropdown-container">
                          <button 
                            className="action-btn redacted-btn ready"
                            onClick={() => setOpenDropdown(openDropdown === recording.vodId ? null : recording.vodId)}
                            data-testid="download-redacted-ready-btn"
                          >
                            <CheckCircle size={16} />
                            Redacted
                            <ChevronDown size={14} className={openDropdown === recording.vodId ? 'rotated' : ''} />
                          </button>
                          {openDropdown === recording.vodId && (
                            <div className="redacted-dropdown">
                              <button onClick={() => downloadRedactedFile(recording.vodId)} title="Download Redacted">
                                <Download size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      ) : processingRedacted.has(recording.vodId) ? (
                        // Show progress bar while processing
                        <button 
                          className="action-btn redacted-btn processing"
                          disabled
                          data-testid="download-redacted-processing-btn"
                        >
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar-fill" 
                              style={{ 
                                width: redactedProgress[recording.vodId] === 'downloading' 
                                  ? '100%' 
                                  : `${Math.max(0, redactedProgress[recording.vodId] || 0)}%` 
                              }}
                            />
                          </div>
                          <span className="progress-text">
                            {redactedProgress[recording.vodId] === -1 
                              ? 'Error' 
                              : redactedProgress[recording.vodId] === 'downloading'
                                ? 'Saving...'
                                : `${Math.round(redactedProgress[recording.vodId] || 0)}%`}
                          </span>
                        </button>
                      ) : (
                        // Show normal button to start processing
                        <button 
                          className="action-btn redacted-btn"
                          onClick={(e) => handleRedactedDownloadClick(e, recording)}
                          title="Download with telemetry cropped"
                          data-testid="download-redacted-btn"
                        >
                          <EyeOff size={16} />
                          Redacted
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="recordings-list">
            <div className="list-header">
              <div className="list-col col-checkbox"></div>
              <div className="list-col col-thumbnail"></div>
              <div className="list-col col-name">Recording</div>
              <div className="list-col col-quality">Quality</div>
              <div className="list-col col-duration">Duration</div>
              <div className="list-col col-size">Size</div>
              <div className="list-col col-actions">Actions</div>
            </div>
            {filteredRecordings.map((recording) => {
              const quality = getQualityLabel(recording.vodName);
              const isSelected = selectedRecordings.has(recording.vodId || recording.vodName);
              return (
                <div 
                  key={recording.vodId || recording.vodName} 
                  className={`list-row ${isSelected ? 'selected' : ''}`}
                >
                  <div 
                    className="list-col col-checkbox"
                    onClick={() => toggleRecordingSelection(recording.vodId)}
                  >
                    {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                  </div>
                  <div className="list-col col-thumbnail">
                    <div className="list-thumbnail" onClick={() => openVideo(recording)}>
                      <ThumbnailImage 
                        recording={recording} 
                        className="list-thumbnail-img"
                        size="small"
                        refreshKey={thumbnailRefreshKey}
                      />
                      <div className="list-thumbnail-overlay">
                        <Play size={20} />
                      </div>
                    </div>
                  </div>
                  <div className="list-col col-name" title={getRecordingDisplayName(recording)}>
                    {getRecordingDisplayName(recording)}
                  </div>
                  <div className="list-col col-quality">
                    <span 
                      className="quality-badge-small"
                      style={{ backgroundColor: getQualityColor(quality) }}
                    >
                      {quality}
                    </span>
                  </div>
                  <div className="list-col col-duration">
                    {formatDuration(recording.duration)}
                  </div>
                  <div className="list-col col-size">
                    {formatFileSize(recording.fileSize)}
                  </div>
                  <div className="list-col col-actions">
                    <button 
                      className="list-action-btn"
                      onClick={() => openVideo(recording)}
                      title="Play"
                    >
                      <Play size={16} />
                    </button>
                    <a 
                      href={getDownloadUrl(recording)}
                      className="list-action-btn"
                      download={recording.vodName}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download Original"
                      onClick={(e) => handleDownloadClick(e, recording)}
                    >
                      <Download size={16} />
                    </a>
                    
                    {/* Redacted button in list view */}
                    {redactedReady[recording.vodId] && !processingRedacted.has(recording.vodId) ? (
                      <div className="list-redacted-dropdown">
                        <button 
                          className="list-action-btn redacted ready"
                          onClick={() => setOpenDropdown(openDropdown === recording.vodId ? null : recording.vodId)}
                          title="Redacted version ready"
                        >
                          <CheckCircle size={16} />
                        </button>
                        {openDropdown === recording.vodId && (
                          <div className="redacted-dropdown list-dropdown">
                            <button onClick={() => downloadRedactedFile(recording.vodId)} title="Download Redacted">
                              <Download size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : processingRedacted.has(recording.vodId) ? (
                      <button 
                        className="list-action-btn redacted processing"
                        disabled
                        title={redactedProgress[recording.vodId] === 'downloading' 
                          ? 'Saving file...' 
                          : `Processing: ${Math.round(redactedProgress[recording.vodId] || 0)}%`}
                      >
                        <div className="list-progress-ring">
                          <svg viewBox="0 0 36 36">
                            <path
                              className="progress-ring-bg"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                              className="progress-ring-fill"
                              strokeDasharray={`${redactedProgress[recording.vodId] === 'downloading' ? 100 : (redactedProgress[recording.vodId] || 0)}, 100`}
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                          </svg>
                        </div>
                      </button>
                    ) : (
                      <button 
                        className="list-action-btn redacted"
                        onClick={(e) => handleRedactedDownloadClick(e, recording)}
                        title="Create redacted version (cropped telemetry)"
                      >
                        <EyeOff size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div 
          className="video-modal-overlay" 
          onClick={(e) => {
            if (!isDragging && !isResizing && !isInteracting) closeVideo();
          }}
        >
          <div
            ref={modalRef}
            className={`video-modal ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
            style={{
              position: 'fixed',
              left: `${modalPosition.x}px`,
              top: `${modalPosition.y}px`,
              width: `${modalSize.width}px`,
              height: `${modalSize.height + 100}px`,
              transform: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="video-modal-header"
              onMouseDown={handleDragStart}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <div className="video-modal-title">
                <Video size={20} />
                <span>{selectedVideo.streamId.toUpperCase()} - Recording</span>
                <span 
                  className="quality-badge"
                  style={{ backgroundColor: getQualityColor(getQualityLabel(selectedVideo.vodName)) }}
                >
                  {getQualityLabel(selectedVideo.vodName)}
                </span>
              </div>
              <button className="video-modal-close" onClick={closeVideo}>
                <X size={24} />
              </button>
            </div>
            
            <div className="video-modal-content" style={{ height: `${modalSize.height}px` }}>
              {videoStatus === 'error' ? (
                <div className="video-error-container">
                  <div className="security-icon-container">
                    <Shield size={100} className="security-icon" />
                  </div>
                  <h3>SECURED CONNECTION REQUIRED</h3>
                  <div className="error-divider"></div>
                  <p className="error-message">ACCESS DENIED</p>
                  <p className="error-hint">
                    You must be connected to the <strong>NSAS VPN</strong><br/>
                    or be physically present at <strong>NSAS Headquarters</strong>
                  </p>
                  <button 
                    className="retry-btn"
                    onClick={() => {
                      setVideoStatus('loading');
                      if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
                      videoTimeoutRef.current = setTimeout(() => {
                        setVideoStatus(prev => prev === 'loading' ? 'error' : prev);
                      }, 15000);
                    }}
                  >
                    Retry Connection
                  </button>
                </div>
              ) : (
                <>
                  {videoStatus === 'loading' && (
                    <div className="video-loading-overlay">
                      <div className="nsas-loader-3d">
                        <img src={NSAS_LOGO} alt="Loading" className="nsas-logo-spin" />
                      </div>
                      <p>Loading video...</p>
                    </div>
                  )}
                  
                  {/* Person Count Overlay */}
                  {personCountEnabled && videoStatus === 'connected' && (
                    <div className="person-count-overlay">
                      <div className={`person-count-badge ${personCountLoading ? 'loading' : ''}`}>
                        <span className="count-label">PERSONS DETECTED</span>
                        <span className="count-number">{personCount !== null ? personCount : '-'}</span>
                        <span className="count-text">{personCount === 1 ? 'Person' : 'Persons'}</span>
                        {personCountConfidence && (
                          <span className={`count-confidence ${personCountConfidence}`}>
                            {personCountConfidence} confidence
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Person Count Toggle Button */}
                  {videoStatus === 'connected' && (
                    <button 
                      className={`person-count-toggle ${personCountEnabled ? 'active' : ''}`}
                      onClick={togglePersonCounting}
                      title={personCountEnabled ? 'Disable AI Person Counting' : 'Enable AI Person Counting'}
                    >
                      👥 AI Count {personCountEnabled ? 'ON' : 'OFF'}
                    </button>
                  )}
                  
                  <video
                    ref={videoRef}
                    key={selectedVideo.vodId}
                    controls
                    autoPlay
                    style={{ width: '100%', height: '100%', backgroundColor: '#000' }}
                    onCanPlay={() => {
                      // Add 3 second delay to show NSAS logo animation
                      setTimeout(() => {
                        setVideoStatus('connected');
                      }, 3000);
                    }}
                    onError={() => setVideoStatus('error')}
                  >
                    <source src={getVideoUrl(selectedVideo)} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </>
              )}
            </div>
            
            <div className="video-modal-footer">
              <span className="video-info">
                {selectedVideo.vodName} | {formatFileSize(selectedVideo.fileSize)} | {formatDuration(selectedVideo.duration)}
              </span>
              <a 
                href={getDownloadUrl(selectedVideo)}
                className="download-link"
                download={selectedVideo.vodName}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => handleDownloadClick(e, selectedVideo)}
              >
                <Download size={16} />
                Download
              </a>
            </div>
            
            <div className="resize-handle" onMouseDown={handleResizeStart}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14ZM14 6H12V4H14V6ZM10 10H8V8H10V10ZM6 14H4V12H6V14Z"/>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-icon">
              <Trash2 size={48} />
            </div>
            <h3>Delete {selectedRecordings.size} Recording{selectedRecordings.size > 1 ? 's' : ''}?</h3>
            <p>This action cannot be undone. The selected recordings will be permanently deleted from the server.</p>
            <div className="delete-confirm-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={deleteSelectedRecordings}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Download PIN Modal */}
      {showPinModal && (
        <div className="pin-modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pin-modal-icon">
              <Lock size={48} />
            </div>
            <h3>Download Authorization</h3>
            <p>Enter PIN code to download this recording on your mobile device.</p>
            
            <div className="pin-input-container">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pinCode}
                onChange={(e) => {
                  setPinCode(e.target.value.replace(/\D/g, ''));
                  setPinError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && pinCode.length === 4) {
                    verifyPinAndDownload();
                  }
                }}
                placeholder="••••"
                className="pin-input"
                autoFocus
              />
              {pinError && <span className="pin-error">{pinError}</span>}
            </div>
            
            <div className="pin-modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowPinModal(false);
                  setPendingDownload(null);
                  setPinCode('');
                  setPinError('');
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-pin-btn"
                onClick={verifyPinAndDownload}
                disabled={pinCode.length !== 4}
              >
                <Download size={18} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notifications */}
      <Toaster 
        position="top-right" 
        richColors 
        expand={true}
        toastOptions={{
          style: {
            background: '#ffffff',
            border: '1px solid #3b82f6',
            color: '#1e293b',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          },
        }}
      />
    </div>
  );
};

export default VideoLibrary;
